import React from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [servicesOpen, setServicesOpen] = React.useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  React.useEffect(() => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="bg-[#0a1628]/90 backdrop-blur-xl rounded-2xl px-6 py-3 border border-white/10">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition">
              <img
                src="/greyfield_data_white_logo-removebg-preview.png"
                alt="Greyfield Data"
                className="h-8 w-auto"
              />
            </Link>
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              <div className="relative">
                <button
                  type="button"
                  aria-expanded={servicesOpen}
                  aria-controls="services-menu"
                  onClick={() => setServicesOpen((open) => !open)}
                  className={`flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-lg transition ${location.pathname.startsWith('/services') || location.pathname === '/ecommerce' ? 'text-white bg-white/10' : 'text-gray-300 hover:text-white'}`}
                >
                  Services
                  <svg className={`h-4 w-4 transition ${servicesOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m6 9 6 6 6-6" /></svg>
                </button>
                {servicesOpen && (
                  <div id="services-menu" className="absolute left-0 mt-2 w-72 rounded-2xl border border-white/10 bg-[#0a1628] p-2 shadow-2xl">
                    <Link to="/services/snowflake-consulting" className="block rounded-xl px-4 py-3 hover:bg-white/5">
                      <span className="block text-sm font-semibold text-white">Snowflake consulting</span>
                      <span className="mt-1 block text-xs leading-relaxed text-gray-400">Pipelines, dbt models, warehouse cost, and account setup</span>
                    </Link>
                    <Link to="/services/sigma-consulting" className="block rounded-xl px-4 py-3 hover:bg-white/5">
                      <span className="block text-sm font-semibold text-white">Sigma consulting</span>
                      <span className="mt-1 block text-xs leading-relaxed text-gray-400">Snowflake models, Sigma workbooks, and dashboard QA</span>
                    </Link>
                    <Link to="/ecommerce" className="block rounded-xl px-4 py-3 text-sm font-semibold text-white hover:bg-white/5">Ecommerce analytics</Link>
                    {isHomePage ? <a href="#services" onClick={() => setServicesOpen(false)} className="block rounded-xl px-4 py-3 text-sm text-gray-300 hover:bg-white/5 hover:text-white">All data services</a> : <Link to="/#services" className="block rounded-xl px-4 py-3 text-sm text-gray-300 hover:bg-white/5 hover:text-white">All data services</Link>}
                  </div>
                )}
              </div>
              <Link to="/case-studies" className={`text-sm font-medium px-4 py-2 rounded-lg transition ${location.pathname.startsWith('/case-studies') ? 'text-white bg-white/10' : 'text-gray-300 hover:text-white'}`}>Case studies</Link>
              {isHomePage ? <a href="#industries" className="text-gray-300 text-sm font-medium hover:text-white px-4 py-2 rounded-lg transition">Industries</a> : <Link to="/#industries" className="text-gray-300 text-sm font-medium hover:text-white px-4 py-2 rounded-lg transition">Industries</Link>}
              <Link
                to="/blog"
                className={`text-sm font-medium px-4 py-2 rounded-lg transition ${
                  location.pathname.startsWith('/blog')
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Blog
              </Link>
              <Link
                to="/resources"
                className={`text-sm font-medium px-4 py-2 rounded-lg transition ${
                  location.pathname.startsWith('/resources') || location.pathname.startsWith('/tools')
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Resources
              </Link>
              <a
                href="https://cal.com/linustse/meeting-with-linus?overlayCalendar=true"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 bg-white text-[#0a1628] px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-100 transition"
              >
                Book a consultation
              </a>
            </div>
            {/* Mobile menu button */}
            <button
              className="md:hidden text-gray-300 focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-2 bg-[#0a1628]/95 backdrop-blur-xl rounded-2xl p-4 border border-white/10">
            <div className="flex flex-col gap-1">
              {isHomePage ? (
                <>
                  <a href="#services" className="px-4 pt-3 text-xs font-semibold uppercase tracking-wider text-gray-500" onClick={() => setMobileMenuOpen(false)}>Services</a>
                </>
              ) : (
                <>
                  <Link to="/#services" className="px-4 pt-3 text-xs font-semibold uppercase tracking-wider text-gray-500" onClick={() => setMobileMenuOpen(false)}>Services</Link>
                </>
              )}
              <Link to="/services/snowflake-consulting" className="text-gray-300 text-sm font-medium hover:text-white hover:bg-white/5 px-4 py-3 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Snowflake consulting</Link>
              <Link to="/services/sigma-consulting" className="text-gray-300 text-sm font-medium hover:text-white hover:bg-white/5 px-4 py-3 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Sigma consulting</Link>
              <Link
                to="/ecommerce"
                className={`text-sm font-medium px-4 py-3 rounded-lg transition ${
                  location.pathname === '/ecommerce'
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Ecommerce analytics
              </Link>
              <Link to="/case-studies" className={`text-sm font-medium px-4 py-3 rounded-lg transition ${location.pathname.startsWith('/case-studies') ? 'text-white bg-white/10' : 'text-gray-300 hover:text-white hover:bg-white/5'}`} onClick={() => setMobileMenuOpen(false)}>Case studies</Link>
              {isHomePage ? <a href="#industries" className="text-gray-300 text-sm font-medium hover:text-white hover:bg-white/5 px-4 py-3 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Industries</a> : <Link to="/#industries" className="text-gray-300 text-sm font-medium hover:text-white hover:bg-white/5 px-4 py-3 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Industries</Link>}
              <Link
                to="/blog"
                className={`text-sm font-medium px-4 py-3 rounded-lg transition ${
                  location.pathname.startsWith('/blog')
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/resources"
                className={`text-sm font-medium px-4 py-3 rounded-lg transition ${
                  location.pathname.startsWith('/resources') || location.pathname.startsWith('/tools')
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Resources
              </Link>
              <Link
                to="/demos"
                className={`text-sm font-medium px-4 py-3 rounded-lg transition ${
                  location.pathname === '/demos'
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Demos
              </Link>
              <a
                href="https://cal.com/linustse/meeting-with-linus?overlayCalendar=true"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 bg-white text-[#0a1628] px-5 py-3 rounded-xl text-sm font-medium text-center hover:bg-gray-100 transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                Book a consultation
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
